import {useCallback, useRef, useState} from 'react';

import type {OmniboxHelpDocsResult, OmniboxResponse} from 'omnibox/omniboxTypes';
import {RealtimeMessageType} from 'realtime/Constants';
import type {RealtimeMessage} from 'realtime/RealtimeChannel';
import useRealtimeChannelSubscription from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForUserId} from 'realtime/util/RealtimeUtil';
import EntityStore from 'stores/EntityStore';

export interface OmniboxStreamState {
  results: OmniboxResponse | null;
  setResults: React.Dispatch<React.SetStateAction<OmniboxResponse | null>>;
  helpDocsResults: Map<string, OmniboxHelpDocsResult | 'loading'>;
  setHelpDocsResults: React.Dispatch<React.SetStateAction<Map<string, OmniboxHelpDocsResult | 'loading'>>>;
  requestIdRef: React.MutableRefObject<string | null>;
}

export default function useOmniboxStream(): OmniboxStreamState {
  const [results, setResults] = useState<OmniboxResponse | null>(null);
  const [helpDocsResults, setHelpDocsResults] = useState<Map<string, OmniboxHelpDocsResult | 'loading'>>(
    new Map()
  );
  const requestIdRef = useRef<string | null>(null);

  const userId = EntityStore.currentUserId;
  const channelName = userId ? getRealtimeChannelNameForUserId(userId) : '';

  const onRealtimeMessage = useCallback((message: RealtimeMessage) => {
    if (
      message.name === RealtimeMessageType.OMNIBOX_PARTIAL ||
      message.name === RealtimeMessageType.OMNIBOX_COMPLETE
    ) {
      if (message.data.requestId !== requestIdRef.current) {
        return;
      }
      setResults(message.data.results);

      if (message.data.results.askSuggestions) {
        setHelpDocsResults((prev) => {
          let changed = false;
          const next = new Map(prev);
          for (const suggestion of message.data.results.askSuggestions) {
            if (suggestion.type === 'help_docs' && !next.has(suggestion.rewrittenQuery)) {
              next.set(suggestion.rewrittenQuery, 'loading');
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }
    } else if (message.name === RealtimeMessageType.OMNIBOX_HELP_DOCS_COMPLETE) {
      if (message.data.requestId !== requestIdRef.current) {
        return;
      }
      setHelpDocsResults((prev) => {
        const next = new Map(prev);
        next.set(message.data.helpDocsResult.rewrittenQuery, message.data.helpDocsResult);
        return next;
      });
    }
  }, []);

  useRealtimeChannelSubscription(channelName, onRealtimeMessage, [
    RealtimeMessageType.OMNIBOX_PARTIAL,
    RealtimeMessageType.OMNIBOX_COMPLETE,
    RealtimeMessageType.OMNIBOX_HELP_DOCS_COMPLETE,
  ]);

  return {results, setResults, helpDocsResults, setHelpDocsResults, requestIdRef};
}
